import { useState, useEffect } from 'react';
import { ShieldCheck, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'react-toastify';
import { AdminFormDrawer } from '@/components/settings/AdminFormDrawer';
import { getIPWhitelist, addToWhitelist, removeFromWhitelist, type WhitelistEntry } from '@/lib/adminApi';

export function IPWhitelistPanel() {
  const [whitelist, setWhitelist] = useState<WhitelistEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [formData, setFormData] = useState({
    ipAddress: '',
    description: '',
  });

  useEffect(() => {
    loadWhitelist();
  }, []);

  const loadWhitelist = async () => {
    try {
      setIsLoading(true);
      const data = await getIPWhitelist();
      setWhitelist(data);
    } catch (error: unknown) {
      console.error('Failed to load whitelist:', error);
      // Don't show toast - table may not exist yet
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddIP = async (e: React.FormEvent) => {
    e.preventDefault();

    // Basic IP validation
    const ipPattern = /^(\d{1,3}\.){3}\d{1,3}$/;
    if (!ipPattern.test(formData.ipAddress)) {
      toast.error('Please enter a valid IPv4 address');
      return;
    }

    if (!formData.description.trim()) {
      toast.error('Please enter a description');
      return;
    }

    try {
      await addToWhitelist({
        ipAddress: formData.ipAddress,
        description: formData.description,
      });
      await loadWhitelist();
      setDrawerOpen(false);
      setFormData({ ipAddress: '', description: '' });
      toast.success(`${formData.ipAddress} added to whitelist`);
    } catch (error: unknown) {
      console.error('Failed to add IP to whitelist:', error);
      toast.error((error as Error)?.message || 'Failed to add IP to whitelist');
    }
  };

  const handleRemoveIP = async (entry: WhitelistEntry) => {
    if (!confirm(`Remove ${entry.ip_address} from whitelist?`)) return;

    try {
      await removeFromWhitelist(entry.id);
      await loadWhitelist();
      toast.success(`${entry.ip_address} removed from whitelist`);
    } catch (error: unknown) {
      console.error('Failed to remove IP from whitelist:', error);
      toast.error((error as Error)?.message || 'Failed to remove IP from whitelist');
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <ShieldCheck className="w-4 h-4 text-green-400" />
          <h3 className="text-sm font-semibold text-white">IP Whitelist</h3>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setDrawerOpen(true)}
          className="h-8 border-border"
        >
          <Plus className="w-3 h-3 mr-1" />
          Add IP
        </Button>
      </div>

      <p className="text-xs text-muted-foreground mb-4">
        Whitelisted IPs bypass all blocking rules including country blocks and rate limits.
        Use for trusted admin IPs, VPN exit nodes, and office networks.
      </p>

      {/* Whitelist */}
      <div className="space-y-2">
        {isLoading ? (
          <div className="animate-pulse space-y-2">
            {[1, 2].map(i => (
              <div key={i} className="h-12 bg-muted rounded" />
            ))}
          </div>
        ) : whitelist.length === 0 ? (
          <div className="text-center py-6 text-muted-foreground text-sm border border-dashed border-border rounded-lg">
            No IPs whitelisted. Add trusted IPs for bypass access.
          </div>
        ) : (
          whitelist.map((entry) => (
            <div
              key={entry.id}
              className="flex items-center justify-between p-3 rounded-lg bg-green-500/5 border border-green-500/20"
            >
              <div>
                <p className="font-mono text-sm text-white">{entry.ip_address}</p>
                <p className="text-xs text-muted-foreground">
                  {entry.description} • Added {new Date(entry.added_at).toLocaleDateString()}
                </p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleRemoveIP(entry)}
                className="h-8 text-red-400 hover:text-red-300"
              >
                <Trash2 className="w-3 h-3" />
              </Button>
            </div>
          ))
        )}
      </div>

      {/* Add IP Drawer */}
      <AdminFormDrawer
        open={drawerOpen}
        onClose={() => {
          setDrawerOpen(false);
          setFormData({ ipAddress: '', description: '' });
        }}
        title="Add IP to Whitelist"
        description="Add a trusted IP address that bypasses all blocking."
      >
        <form onSubmit={handleAddIP} className="space-y-4">
          <div className="space-y-2">
            <Label>IP Address</Label>
            <Input
              value={formData.ipAddress}
              onChange={(e) => setFormData({ ...formData, ipAddress: e.target.value })}
              placeholder="e.g., 192.168.1.100"
              className="bg-background border-border font-mono"
              required
            />
            <p className="text-xs text-muted-foreground">
              Enter a valid IPv4 address. CIDR notation is not currently supported.
            </p>
          </div>

          <div className="space-y-2">
            <Label>Description</Label>
            <Input
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="e.g., Office main IP, Admin VPN"
              className="bg-background border-border"
              required
            />
          </div>

          <div className="flex gap-3 pt-4">
            <Button type="submit" className="flex-1 bg-[#8FB560] hover:bg-[#7a9d51]">
              <ShieldCheck className="w-4 h-4 mr-2" />
              Add to Whitelist
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => setDrawerOpen(false)}
            >
              Cancel
            </Button>
          </div>
        </form>
      </AdminFormDrawer>
    </div>
  );
}
